//-- Copyright (c) 2021 TMT Insights Group, LLC
import axios from '@/_utils/axios';
import { LoadingStatuses } from '@/constants/constants';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ConstantsState, JobDetail, JobState, PolarisApiResponse, JobDetailInventoryFile } from './slice_types';
import { RootState } from '@/redux/store';

const initialState: JobState = {
  data: {} as JobState['data'],
  relatedFiles: {} as JobState['relatedFiles'],
  pagination: {} as JobState['pagination'],
  status: LoadingStatuses.fulfilled
};

// THUNKS
export const getJob = createAsyncThunk('job/getJob', async ({ id, refresh }: { id: string | number; refresh?: boolean }, { getState }) => {
  // since the reducers don't have access to global state but we are able to retrieve from the store, we can pass it in as a parameter
  return axios.get(`/core/jobs/${id}/${refresh ? '?refresh=true' : ''}`).then(
    (
      res
    ): {
      data: JobDetail;
      success: boolean;
      constants: ConstantsState;
    } => ({ ...res.data, constants: (getState() as RootState).constants })
  );
});

export const updateJobDueDate = createAsyncThunk('job/updateJobDueDate', async ({ id, value }: { id: string | number; value: string }) => {
  return axios.post(`/core/jobs/${id}/`, { due_date: value }).then((res): { data: JobDetail } => ({ data: res.data?.data }));
});

export const updateJobEmbargoDate = createAsyncThunk('job/updateJobEmbargoDate', async ({ id, value }: { id: string | number; value: string }) => {
  return axios.post(`/core/jobs/${id}/`, { embargo_date: value }).then((res): { data: JobDetail } => ({ data: res.data?.data }));
});

export const releaseJobEmbargoDate = createAsyncThunk('job/releaseJobEmbargoDate', async ({ id }: { id: string | number }) => {
  return axios
    .post(`/core/jobs/${id}/embargo_resume/`, {}, { ignoredGlobalCatch: true })
    .then((res): { data: JobDetail } => ({ data: res.data?.data }));
});

export const getJobRelatedFiles = createAsyncThunk(
  'job/getJobRelatedFiles',
  async ({ id, showAll, pageSize, pageNum }: { id: string; showAll: boolean; pageSize: number; pageNum: number }, { getState }) => {
    const fileTypeGroupings = (getState() as RootState).constants.commonConstants!.fileTypeGrouping;
    const { video, audio, subtitle, image, other } = fileTypeGroupings;

    return axios
      .post(`/core/jobs/${id}/rally_inventory/?show_all=${showAll}&page_size=${pageSize}&page_num=${pageNum}`, {
        file_type_filter: [...video, ...audio, ...subtitle, ...image, ...other]
      })
      .then((res): PolarisApiResponse<JobDetailInventoryFile[]> => res.data);
  }
);

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {
    clearJob: (state) => {
      state.data = {} as JobState['data'];
      state.relatedFiles = {} as JobState['relatedFiles'];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getJob.pending, (state) => {
      state.status = LoadingStatuses.pending;
    });
    builder.addCase(getJob.fulfilled, (state, { payload: { data } }) => {
      // there is a builder.addCase for this in dataTableSlice to handle tasks data
      const parsedData = data || [];
      parsedData.task_timeline = data.task_timeline.map((milestone: any) => {
        let task = null;
        if (milestone.tasks?.length === 1) {
          task = milestone.tasks[0];
        } else {
          task = milestone.tasks?.find((task: any) => task.status === 'in_progress' || !task.completed_at) || milestone.tasks.at(-1);
        }
        return { ...milestone, task, status: task.status };
      });
      state.data = parsedData;
      state.status = LoadingStatuses.fulfilled;
    });
    builder.addCase(getJob.rejected, (state) => {
      state.status = LoadingStatuses.rejected;
    });
    builder.addCase(updateJobDueDate.fulfilled, (state, { payload: { data } }) => {
      (state as any).data.due_date = data.due_date;
    });
    builder.addCase(updateJobEmbargoDate.fulfilled, (state, { payload: { data } }) => {
      (state as any).data.embargo_date = data.embargo_date;
    });
    builder.addCase(releaseJobEmbargoDate.fulfilled, (state, { payload: { data } }) => {
      state.data = { ...data, task_timeline: state.data.task_timeline };
    });
    builder.addCase(getJobRelatedFiles.pending, (state) => {
      state.relatedFiles.status = LoadingStatuses.pending;
    });
    builder.addCase(getJobRelatedFiles.fulfilled, (state, { payload }) => {
      state.relatedFiles.data = payload.data || [];
      state.pagination = payload.pagination;
      state.relatedFiles.status = LoadingStatuses.fulfilled;
    });
    builder.addCase(getJobRelatedFiles.rejected, (state) => {
      state.relatedFiles.status = LoadingStatuses.rejected;
    });
  }
});

export const { clearJob } = jobSlice.actions;

export default jobSlice.reducer;
